import React, { useState, useEffect } from "react";
import { compose } from "react-apollo";
import { withQuery } from "containers/withQuery";
import styled from "styled-components";
import Input from "components/Input/Input";
import Modal from "components/Modal/Modal";
import Field from "components/Field/Field";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import Footer from "../modalComponents/Footer";
import { arrowPositionTypes, plantLocationTypes } from "../enumTypes";
import { Select } from "../CustomSelect";
import { MultiSelect } from "../MultiSelect";
import {
  INVENTORY_ITEMS_QUERY,
  ARROW_POSITIONS_QUERY,
  LOCATION_TYPES_QUERY,
  DEVICES_QUERY,
} from "../generalRegisterQueries";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 35px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  input[type="checkbox"] {
    width: 22px;
    height: 22px;
    cursor: pointer;
  }

  label {
    margin: 0;
    font-weight: 500;
  }
`;

const LOCATION_TYPE_RULES = {
  AGGREGATES: {
    manyColumns: true,
    handleItems: true,
  },
  GENERAL: {
    manyColumns: true,
    handleItems: true,
  },
  ACTUATOR: {
    manyColumns: true,
    handleItems: false,
  },
};

const inferRules = (locationType) => (
  (locationType && LOCATION_TYPE_RULES[locationType])
    ? LOCATION_TYPE_RULES[locationType]
    : { manyColumns: false, handleItems: false }
);

const isEmpty = v => (v === null || v === undefined || v === "");

const buildInitialState = ({
  name,
  arrowPosition,
  inventoryItems,
  isContainerVertical,
  locationType,
  numberOfColumns,
  positionX,
  positionY,
}) => ({
  name,
  arrowPosition,
  isContainerVertical,
  locationType,
  numberOfColumns: numberOfColumns || 1,
  positionX,
  positionY,
  inventoryItemIds: (inventoryItems && inventoryItems.length > 0) ? inventoryItems.map(({ id }) => id) : [],
});

const PlantLocationModal = ({
  name,
  actuators,
  arrowPosition,
  inventoryItems,
  isContainerVertical,
  locationType,
  numberOfColumns,
  positionX,
  positionY,
  snackbarContext,
  onSave,
  isOpen,
  toggleModal,
  title,
  locationTypes: {
    states: locationTypesStates,
  },
  arrowPositions: {
    states: arrowPositionsStates,
  },
  inventoryItemsList,
  devices,
}) => {
  const [form, setForm] = useState(
    buildInitialState({
      name,
      actuators,
      arrowPosition,
      inventoryItems,
      isContainerVertical,
      locationType,
      numberOfColumns,
      positionX,
      positionY,
    })
  );

  const rules = inferRules(form.locationType);

  const arrowPositionOptions = [
    {
      value: null,
      isDisabled: true,
    },
    ...arrowPositionsStates.map(({ name: stateName }) => ({
      value: stateName,
      label: arrowPositionTypes[stateName],
    })),
  ];

  const locationTypeOptions = [
    {
      value: null,
      isDisabled: true,
    },
    ...locationTypesStates.map(({ name: stateName }) => ({
      value: stateName,
      label: plantLocationTypes[stateName],
    })),
  ];

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState({
        name,
        actuators,
        arrowPosition,
        inventoryItems,
        isContainerVertical,
        locationType,
        numberOfColumns,
        positionX,
        positionY,
      }));
    }
  }, [
    isOpen,
    name,
    actuators,
    arrowPosition,
    inventoryItems,
    isContainerVertical,
    locationType,
    numberOfColumns,
    positionX,
    positionY
  ]);

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const onSaveEntry = () => {
    if (
      !form.name.trim()
      || isEmpty(form.arrowPosition)
      || isEmpty(form.locationType)
      || isEmpty(form.numberOfColumns)
      || isEmpty(form.positionX)
      || isEmpty(form.positionY)
    ) {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        "Os campos nome, tipo, lado de referência, colunas e posições são obrigatórios."
      );
      return;
    }

    onSave({
      ...form,
      positionX: parseFloat(form.positionX),
      positionY: parseFloat(form.positionY),
      deviceId: devices[0].id,
      numberOfColumns: rules.manyColumns
        ? parseInt(form.numberOfColumns, 10)
        : 1,
      inventoryItemIds: rules.handleItems
        ? form.inventoryItemIds
        : [],
      isContainerVertical: rules.manyColumns
        ? form.isContainerVertical
        : false,
    });
  };

  const updateSelect = (key) => (_, option) => {
    setForm(prev => ({
      ...prev,
      [key]: option ? option.value : null
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      toggleModal={toggleModal}
      size="lg"
      title={title}
      footer={<Footer onConfirm={onSaveEntry} />}
    >
      <Row>
        <Field title="Nome" width="48%">
          <Input
            name="name"
            style={{ minHeight: "40px" }}
            value={form.name}
            onChange={updateField}
          />
        </Field>
        <Field title="Tipo" width="48%">
          <Select
            name="locationType"
            placeholder="Escolha..."
            value={locationTypeOptions.find(o => o.value === form.locationType) || null}
            onChange={updateSelect("locationType")}
            options={locationTypeOptions}
          />
        </Field>
      </Row>
      {(rules && rules.handleItems) && (
        <Row>
          <Field title="Materiais" width="100%">
            <MultiSelect
              name="inventoryItems"
              style={{ minHeight: "40px" }}
              placeholder="Escolha..."
              options={inventoryItemsList}
              value={form.inventoryItemIds}
              onChange={(ids) => updateField("inventoryItemIds", ids)}
            />
          </Field>
        </Row>
      )}
      <Row>
        <Field title="Lado de Referência" width="32%">
          <Select
            name="arrowPosition"
            placeholder="Escolha..."
            value={arrowPositionOptions.find(o => o.value === form.arrowPosition) || null}
            onChange={updateSelect("arrowPosition")}
            options={arrowPositionOptions}
          />
        </Field>
        <Field title="Posição X" width="32%">
          <Input
            name="positionX"
            type="number"
            style={{ minHeight: "40px" }}
            value={form.positionX}
            onChange={updateField}
          />
        </Field>
        <Field title="Posição Y" width="32%">
          <Input
            name="positionY"
            type="number"
            style={{ minHeight: "40px" }}
            value={form.positionY}
            onChange={updateField}
          />
        </Field>
      </Row>
      {(rules && rules.manyColumns) && (
        <Row>
          <Field title="Colunas" width="48%">
            <Input
              name="numberOfColumns"
              style={{ minHeight: "40px" }}
              type="number"
              min={1}
              step={1}
              value={form.numberOfColumns}
              onChange={updateField}
            />
          </Field>
          <Field title="Expansão Vertical" width="48%">
            <CheckboxRow>
              <div style={{ paddingLeft: 20, paddingBottom: 5 }}>
                <Input
                  name="isContainerVertical"
                  type="checkbox"
                  checked={!!form.isContainerVertical}
                  onChange={updateField}
                />
              </div>
            </CheckboxRow>
          </Field>
        </Row>
      )}
    </Modal>
  );
};

export default compose(
  withQuery({
    query: INVENTORY_ITEMS_QUERY,
    props: ({ data }) => ({
      inventoryItemsList: data.inventoryItems,
    }),
  }),
  withQuery({ query: ARROW_POSITIONS_QUERY }),
  withQuery({ query: LOCATION_TYPES_QUERY }),
  withQuery({ query: DEVICES_QUERY }),
  withSnackbarConsumer,
)(PlantLocationModal);
